// src/types/superadmin.types.ts
import { FirebaseDate } from './firebase.types';

// ─── Módulos del sistema ────────────────────────────────────────────────────
export type GymModuleId =
  | 'members'        // Gestión de Socios (base)
  | 'payments'       // Cuotas Mensuales y Pagos (base)
  | 'settings'       // Configuración y Perfil (base)
  | 'attendance'     // Control de Asistencia / Kiosco / DNI
  | 'daily_cash'     // Caja Diaria (Cobros de Productos/Ventas)
  | 'products'       // Módulo de Productos (Inventario)
  | 'classes'        // Clases Grupales y Horarios
  | 'routines'       // Ejercicios y Rutinas de Socios
  | 'reports'        // Reportes y Dashboard Financiero
  | 'qr_registration'; // Auto-registro por QR de Socios

export interface GymModule {
  id: GymModuleId;
  label: string;
  description: string;
  isBase: boolean; // Si es base, siempre habilitado y no editable
  icon: string;    // Emoji o nombre de ícono para la UI
}

/** Lista completa de módulos disponibles en el sistema */
export const ALL_MODULES: GymModule[] = [
  { id: 'members',         label: 'Gestión de Socios',                    description: 'Alta, baja, modificación y búsqueda de socios',          isBase: true,  icon: '👥' },
  { id: 'payments',        label: 'Cuotas Mensuales y Pagos',             description: 'Control de deudas, historial de pagos y cobranzas',      isBase: true,  icon: '💳' },
  { id: 'settings',        label: 'Configuración y Membresías',           description: 'Gestión de actividades, planes y perfil del gimnasio',   isBase: true,  icon: '⚙️' },
  { id: 'attendance',      label: 'Control de Asistencia (Kiosco / DNI)', description: 'Registro de asistencia por DNI, QR o huella dactilar',  isBase: false, icon: '📋' },
  { id: 'daily_cash',      label: 'Caja Diaria',                         description: 'Cobros de productos, ventas y caja del día',            isBase: false, icon: '💰' },
  { id: 'products',        label: 'Módulo de Productos (Inventario)',     description: 'Gestión de stock, productos y ventas',                  isBase: false, icon: '📦' },
  { id: 'classes',         label: 'Clases Grupales y Horarios',           description: 'Programación de clases y gestión de horarios',          isBase: false, icon: '🗓️' },
  { id: 'routines',        label: 'Ejercicios y Rutinas de Socios',       description: 'Planes de entrenamiento personalizados',                isBase: false, icon: '🏋️' },
  { id: 'reports',         label: 'Reportes y Dashboard Financiero',      description: 'Estadísticas, gráficos e informes exportables',         isBase: false, icon: '📊' },
  { id: 'qr_registration', label: 'Auto-registro por QR de Socios',      description: 'Los socios se registran escaneando un código QR',       isBase: false, icon: '📱' },
];

/** Módulos habilitados en un tier básico (retrocompatibilidad) */
export const TIER_BASIC_MODULES: GymModuleId[] = ['members', 'payments', 'settings'];

/** Módulos habilitados en un tier completo (retrocompatibilidad) */
export const TIER_FULL_MODULES: GymModuleId[] = ALL_MODULES.map(m => m.id);

/**
 * Obtiene los módulos habilitados a partir de un plan.
 * Si tiene enabledModules, los usa; si no, hace fallback al tier.
 */
export const resolveEnabledModules = (
  enabledModules?: string[],
  tier?: 'basic' | 'full' | string
): GymModuleId[] => {
  if (enabledModules && enabledModules.length > 0) {
    return enabledModules as GymModuleId[];
  }
  if (tier === 'basic') return TIER_BASIC_MODULES;
  // 'full' o cualquier otro valor → todos los módulos
  return TIER_FULL_MODULES;
};

// Tipo para los planes de suscripción al sistema
export interface SubscriptionPlan {
  id: string;
  name: string;
  description: string;
  duration: number; // días
  price: number;
  features: string[];
  isActive: boolean;
  tier?: 'basic' | 'full'; // Mantenido para retrocompatibilidad
  enabledModules?: GymModuleId[]; // 🆕 Módulos habilitados (toma precedencia sobre tier)
  maxGyms?: number; // número máximo de gimnasios permitidos (opcional)
  maxAdmins?: number; // número máximo de administradores permitidos (opcional)
  maxUsers?: number; // número máximo de usuarios permitidos (opcional)
  maxMembers?: number; // número máximo de miembros permitidos (opcional)
  createdAt?: FirebaseDate;
  updatedAt?: FirebaseDate;
}

// Tipo para las suscripciones de gimnasios al sistema
export interface GymSubscription {
  id: string;
  gymId: string;
  gymName: string;
  planId: string;
  planName: string;
  startDate: FirebaseDate;
  endDate: FirebaseDate;
  price: number;
  status: 'active' | 'pending' | 'expired' | 'cancelled';
  paymentMethod: string;
  paymentDate: FirebaseDate;
  renewalRequested: boolean;
  autoRenewal: boolean;
  tier?: 'basic' | 'full'; // Mantenido para retrocompatibilidad
  enabledModules?: GymModuleId[]; // 🆕 Módulos habilitados del plan asignado
  lastRenewalDate?: FirebaseDate;
  nextRenewalDate?: FirebaseDate;
  trialEndDate?: FirebaseDate;
  notes?: string;
  createdAt?: FirebaseDate;
  updatedAt?: FirebaseDate;
}

// Tipo para los pagos de suscripciones
// Tipo para los pagos de suscripciones
export interface Payment {
  id: string;
  subscriptionId: string;
  gymId: string;
  gymName: string;
  amount: number;
  date: FirebaseDate;
  method: 'transfer' | 'mercadopago' | 'card' | 'cash' | 'other';
  status: 'completed' | 'pending' | 'failed';
  reference?: string;
  notes?: string;
  description?: string;
  createdAt?: FirebaseDate;
  updatedAt?: FirebaseDate;
}

// Tipo para los gimnasios
export interface Gym {
  id: string;
  name: string;
  owner: string;
  email: string;
  phone: string;
  cuit: string;
  status: 'active' | 'trial' | 'suspended';
  registrationDate: FirebaseDate;
  trialEndsAt?: FirebaseDate;
  subscriptionData?: {
    plan: string;
    startDate: FirebaseDate;
    endDate: FirebaseDate;
    price: number;
    paymentMethod: string;
    lastPayment: FirebaseDate;
    renewalRequested: boolean;
    memberLimit?: number;
    tier?: 'basic' | 'full'; // Mantenido para retrocompatibilidad
    enabledModules?: GymModuleId[]; // 🆕 Módulos habilitados copiados del plan
  };
  address?: string;
  website?: string;
  socialMedia?: string;
  logo?: string;
  totalDebt?: number;
  lastLoginDate?: FirebaseDate;
  loginHistory?: FirebaseDate[];
  activeUsers?: number;
  totalMembers?: number;
  internalNotes?: string;
  kioskSettings?: {
    backgroundUrl?: string;
    enableSounds?: boolean;
  };
  createdAt?: FirebaseDate;
  updatedAt?: FirebaseDate;
}

// Tipo para las estadísticas del dashboard del superadmin
export interface SuperadminStats {
  totalGyms: number;
  activeGyms: number;
  trialGyms: number;
  suspendedGyms: number;
  totalRevenue: number;
  revenueThisMonth: number;
  pendingPayments: number;
  newGymsThisMonth: number;
  topGymsByRevenue?: {
    id: string;
    name: string;
    revenue: number
  }[];
  gymsByStatus?: {
    status: string;
    count: number
  }[];
}

// Tipo para el gráfico de suscripciones
export interface SubscriptionChartData {
  dates: string[];
  values: {
    newSubscriptions: number[];
    renewals: number[];
    total: number[];
  };
}

// Tipo para el gráfico de ingresos
export interface RevenueChartData {
  dates: string[];
  values: {
    amount: number[];
    cumulative: number[];
  };
}

// Objetos con funciones útiles 
export const subscriptionTypes = {
  createEmptySubscriptionPlan: (): SubscriptionPlan => ({
    id: '',
    name: '',
    description: '',
    duration: 30,
    price: 0,
    features: [],
    isActive: true,
    tier: 'full'
  }),

  createEmptyGymSubscription: (): GymSubscription => ({
    id: '',
    gymId: '',
    gymName: '',
    planId: '',
    planName: '',
    startDate: new Date(),
    endDate: new Date(),
    price: 0,
    status: 'pending',
    paymentMethod: '',
    paymentDate: new Date(),
    renewalRequested: false,
    autoRenewal: false
  }),

  createEmptyPayment: (): Payment => ({
    id: '',
    subscriptionId: '',
    gymId: '',
    gymName: '',
    amount: 0,
    date: new Date(),
    method: 'transfer',
    status: 'pending'
  }),

  createEmptyGym: (): Gym => ({
    id: '',
    name: '',
    owner: '',
    email: '',
    phone: '',
    cuit: '',
    status: 'trial',
    internalNotes: '',
    registrationDate: new Date()
  }),

  // Duración de los planes en opciones para select
  durationOptions: [
    { value: 7, label: '7 días' },
    { value: 15, label: '15 días' },
    { value: 30, label: '1 mes' },
    { value: 90, label: '3 meses' },
    { value: 180, label: '6 meses' },
    { value: 365, label: '1 año' }
  ],

  // Métodos de pago disponibles
  paymentMethods: [
    { value: 'transfer', label: '🏦 Transferencia Bancaria (Banco Nación)' },
    { value: 'mercadopago', label: '💳 Mercado Pago' },
    { value: 'card', label: 'Tarjeta de Crédito/Débito' },
    { value: 'cash', label: 'Efectivo' },
    { value: 'other', label: 'Otro' }
  ],

  // Estados de los gimnasios
  gymStatuses: [
    { value: 'active', label: 'Activo' },
    { value: 'trial', label: 'En Prueba' },
    { value: 'suspended', label: 'Suspendido' }
  ]
};

// Interfaz para la configuración de la Landing Page
export interface LandingSettings {
  address: string;
  phone: string;
  whatsapp: string;
  email: string;
  facebookUrl: string;
  instagramUrl: string;
  twitterUrl: string;
  linkedinUrl: string;
  googlePlayUrl: string;
  appStoreUrl: string;
  windowsAppUrl: string; // Enlace de app Windows
  showFacebook: boolean;
  showInstagram: boolean;
  showTwitter: boolean;
  showLinkedin: boolean;
  heroTitle: string;
  heroSubtitle: string;
}

// Interfaz para la configuración de Correo (SMTP)
export interface SmtpSettings {
  host: string;
  port: string;
  user: string;
  pass: string;
  secure: boolean;
  senderName: string;
  senderEmail: string;
}

